"use client"

import React, { useEffect } from "react"
import SimpleModal from "./BaseModal"
import { Check, Loader2 } from "lucide-react"
import Image from "next/image"
import { Session } from "next-auth"
import { toast } from "sonner"
import { useVerifyEmailMutation } from "@/hooks/useVerifyEmailMutation"

type Props = {
   isOpen: boolean
   onClose: () => void
   user?: Session["user"]
   onVerifiedSuccess?: () => void
}

const VerifyEmail = ({ isOpen, onClose, user, onVerifiedSuccess }: Props) => {
   // Pass `isOpen` so the rate-limit status probe (an authenticated API call) only
   // runs while the modal is open — never on mount for a closed modal, which would
   // 401 when the session is missing/stale (e.g. on /profile after the cookie is gone).
   const { trigger, isPending, isDisabled, hasSent, getButtonText } = useVerifyEmailMutation(user?.email, isOpen)

   useEffect(() => {
      if (isOpen && user?.emailVerified) {
         onClose()
         onVerifiedSuccess?.()
         toast.success("Email verified successfully! Welcome aboard.")
      }
   }, [user?.emailVerified, isOpen, onClose, onVerifiedSuccess])

   // Once a link has been sent this session, the action becomes a resend.
   const defaultButtonText = hasSent ? "Resend Verification Email" : "Verify Email Address"

   const membershipBenefits = [
      "Member benefits & discounts",
      "Complimentary job postings",
      "Resources & downloadable guides",
      "Events, webinars & training",
      "Industry news & updates",
      "Member-only opportunities",
   ]

   return (
      <SimpleModal
         isOpen={isOpen}
         onClose={onClose}
         classname="max-w-2xl p-6 md:p-8 overflow-y-auto"
         skippable={!isPending}
      >
         <div className="flex flex-col items-center text-center">
            <div className="relative mb-2 flex h-16 w-16 items-center justify-center">
               <Image
                  src="/_images/verify-email.svg"
                  alt="Verify Email"
                  width={64}
                  height={64}
                  priority
                  className="object-contain"
               />
            </div>

            <h2 className="mb-2 text-custom-lg-bold sm:text-custom-2xl-bold">Verify Your Email Address</h2>

            <p className="mb-6 max-w-lg text-custom-base-normal">
               You&apos;re one step away from unlocking your full Restaurant Association membership experience.
            </p>

            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
               {membershipBenefits.map((benefit, index) => (
                  <div
                     key={index}
                     className="flex items-center gap-3 rounded-lg border border-blue-50 bg-blue-50 p-3 text-left "
                  >
                     <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-emerald-600">
                        <Check />
                     </span>
                     <span className="text-custom-sm-medium">{benefit}</span>
                  </div>
               ))}
            </div>

            <div className="mt-6 w-full rounded-lg border border-orange-100 bg-orange-50/50 p-2">
               <p className="text-custom-sm-medium text-amber-800">
                  Please verify your email within the next 60 days to avoid interruptions to your membership benefits.
               </p>
            </div>

            <div className="mt-6 flex w-full flex-col items-center gap-4">
               <button
                  onClick={trigger}
                  disabled={isDisabled}
                  className="flex w-full max-w-xs items-center justify-center gap-2 rounded-lg bg-custom-primary px-4 py-3 text-custom-sm-medium text-white transition-opacity disabled:opacity-60"
               >
                  {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {getButtonText(defaultButtonText)}
               </button>
            </div>
         </div>
      </SimpleModal>
   )
}

export default VerifyEmail
